import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import QRCode from "qrcode";
import { SheqPpeRecord } from "../../../redux/sheqApi";

const NAVY_R = 18;
const NAVY_G = 52;
const NAVY_B = 86;

export interface PpeSlipOptions {
  /** All active PPE records to include in the slip table */
  records: SheqPpeRecord[];
  /** Captured signature as PNG data URL (from signature canvas) */
  signatureDataUrl?: string;
  /** SHEQ / issuing officer name */
  sheqOfficerName?: string;
  /** Company logo as base64 data URL */
  logoDataUrl?: string;
  /** Site name override (falls back to records[0].blockName) */
  siteName?: string;
}

/**
 * Generates and downloads a jsPDF PPE Acknowledgement Slip.
 * Call from a browser context (uses window.location, document.save).
 */
export async function generatePpeSlip(opts: PpeSlipOptions): Promise<void> {
  const { records, signatureDataUrl, sheqOfficerName, logoDataUrl, siteName } =
    opts;
  if (records.length === 0) return;

  const primary = records[0];
  const printDate = new Date().toLocaleDateString("en-ZA", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const margin = 14;
  let y = margin;

  // ── Header ────────────────────────────────────────────────────────────
  // Logo (top-left)
  if (logoDataUrl) {
    try {
      doc.addImage(logoDataUrl, "PNG", margin, y, 22, 22);
    } catch {
      // ignore missing logo
    }
  }

  // QR code (top-right) — links to the worker's PPE records
  const qrTarget = `${window.location.origin}/coms/sheq?workerId=${encodeURIComponent(primary.workerId)}`;
  let qrDataUrl: string | undefined;
  try {
    qrDataUrl = await QRCode.toDataURL(qrTarget, { width: 80, margin: 1 });
  } catch {
    // no QR if generation fails
  }
  if (qrDataUrl) {
    doc.addImage(qrDataUrl, "PNG", pageW - margin - 22, y, 22, 22);
  }

  // Title centred
  doc.setFontSize(15);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(NAVY_R, NAVY_G, NAVY_B);
  doc.text("PPE ACKNOWLEDGEMENT SLIP", pageW / 2, y + 8, { align: "center" });

  doc.setFontSize(8.5);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(90, 90, 90);
  doc.text("WestProp VMS · Health & Safety Management", pageW / 2, y + 15, {
    align: "center",
  });

  const site = siteName ?? primary.blockName;
  if (site) {
    doc.text(`Site / Block: ${site}`, pageW / 2, y + 21, { align: "center" });
  }

  y += 29;

  // Rule
  doc.setDrawColor(NAVY_R, NAVY_G, NAVY_B);
  doc.setLineWidth(0.6);
  doc.line(margin, y, pageW - margin, y);
  y += 6;

  // ── Worker info box ────────────────────────────────────────────────────
  const boxH = primary.department ? 20 : 16;
  doc.setFillColor(236, 242, 248);
  doc.roundedRect(margin, y, pageW - 2 * margin, boxH, 2, 2, "F");

  doc.setFontSize(7.5);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(NAVY_R, NAVY_G, NAVY_B);
  doc.text("WORKER INFORMATION", margin + 3, y + 5);

  doc.setFont("helvetica", "normal");
  doc.setTextColor(30, 30, 30);
  doc.setFontSize(9);

  const line2Y = y + 12;
  doc.text(`Name:  ${primary.workerName}`, margin + 3, line2Y);
  if (primary.workerEmployeeNo) {
    doc.text(`Emp. No:  ${primary.workerEmployeeNo}`, margin + 75, line2Y);
  }
  if (primary.department) {
    doc.text(
      `Department:  ${primary.department}`,
      margin + 3,
      y + boxH - 3,
    );
  }

  y += boxH + 7;

  // ── PPE table ──────────────────────────────────────────────────────────
  doc.setFontSize(8.5);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(NAVY_R, NAVY_G, NAVY_B);
  doc.text("PPE ISSUED", margin, y);
  y += 3;

  autoTable(doc, {
    startY: y,
    head: [
      [
        "Item",
        "Code",
        "Size",
        "Qty",
        "Batch / Serial No.",
        "Issue Date",
        "Replacement Due",
        "Status",
      ],
    ],
    body: records.map((r) => [
      r.ppeName,
      r.ppeCode ?? "—",
      r.size ?? "—",
      String(r.quantity),
      r.batchOrSerialNumber ?? "—",
      r.issuedDate,
      r.replacementDueDate,
      r.status,
    ]),
    styles: { fontSize: 8, cellPadding: 2.5 },
    headStyles: {
      fillColor: [NAVY_R, NAVY_G, NAVY_B],
      textColor: 255,
      fontStyle: "bold",
      fontSize: 8,
    },
    alternateRowStyles: { fillColor: [248, 251, 254] },
    columnStyles: {
      0: { cellWidth: 44 },
      1: { cellWidth: 14 },
      2: { cellWidth: 12 },
      3: { cellWidth: 9 },
      4: { cellWidth: 32 },
      5: { cellWidth: 21 },
      6: { cellWidth: 22 },
      7: { cellWidth: 18 },
    },
    margin: { left: margin, right: margin },
    theme: "grid",
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  y = (doc as any).lastAutoTable.finalY + 8;

  // ── Declaration ────────────────────────────────────────────────────────
  const declText =
    '"I confirm receipt of the above personal protective equipment and undertake ' +
    "to use it correctly, store it appropriately, and report any defects, damage " +
    'or loss to my supervisor immediately."';
  const declLines = doc.splitTextToSize(declText, pageW - 2 * margin - 8);
  const declH = declLines.length * 5 + 12;

  doc.setFillColor(255, 252, 230);
  doc.setDrawColor(210, 180, 0);
  doc.setLineWidth(0.3);
  doc.roundedRect(margin, y, pageW - 2 * margin, declH, 2, 2, "FD");

  doc.setFontSize(8);
  doc.setFont("helvetica", "italic");
  doc.setTextColor(70, 55, 0);
  doc.text(declLines, margin + 4, y + 6);
  y += declH + 8;

  // ── Signature section ──────────────────────────────────────────────────
  const colW = (pageW - 2 * margin - 6) / 2;
  const sigH = 34;

  // Staff signature
  doc.setDrawColor(160, 160, 160);
  doc.setLineWidth(0.3);
  doc.rect(margin, y, colW, sigH);

  doc.setFontSize(7.5);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(NAVY_R, NAVY_G, NAVY_B);
  doc.text("Staff Signature", margin + 2, y + 5);

  if (signatureDataUrl) {
    try {
      doc.addImage(
        signatureDataUrl,
        "PNG",
        margin + 2,
        y + 7,
        colW - 4,
        20,
      );
    } catch {
      // blank signature area
    }
  } else {
    // blank signature line
    doc.setDrawColor(190, 190, 190);
    doc.setLineWidth(0.4);
    doc.line(margin + 4, y + 24, margin + colW - 4, y + 24);
  }

  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor(60, 60, 60);
  doc.text(`Print Name: ${primary.workerName}`, margin + 2, y + sigH - 5);
  doc.text(`Date: ${printDate}`, margin + colW / 2, y + sigH - 5);

  // SHEQ officer
  const officerX = margin + colW + 6;
  doc.setDrawColor(160, 160, 160);
  doc.setLineWidth(0.3);
  doc.rect(officerX, y, colW, sigH);

  doc.setFontSize(7.5);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(NAVY_R, NAVY_G, NAVY_B);
  doc.text("SHEQ / Issuing Officer Signature", officerX + 2, y + 5);

  // signature line
  doc.setDrawColor(190, 190, 190);
  doc.setLineWidth(0.4);
  doc.line(officerX + 4, y + 24, officerX + colW - 4, y + 24);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor(60, 60, 60);
  const officerDisplay =
    sheqOfficerName ?? primary.issuedByName ?? "____________________";
  doc.text(`Print Name: ${officerDisplay}`, officerX + 2, y + sigH - 5);
  doc.text(`Date: ${printDate}`, officerX + colW / 2, y + sigH - 5);

  y += sigH + 8;

  // ── Footer ─────────────────────────────────────────────────────────────
  doc.setFontSize(6.5);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(150, 150, 150);
  const footerIds = records.map((r) => r.id?.slice(-8) ?? "").join(", ");
  doc.text(
    `Generated: ${printDate} · WestProp VMS SHEQ Module · Record ref: ${footerIds}`,
    pageW / 2,
    y,
    { align: "center" },
  );
  doc.line(margin, y - 3, pageW - margin, y - 3);

  // ── Download ───────────────────────────────────────────────────────────
  const safeName = primary.workerName.replace(/[^a-zA-Z0-9]/g, "_");
  const dateStr = new Date().toISOString().slice(0, 10);
  doc.save(`PPE_Slip_${safeName}_${dateStr}.pdf`);
}
